import React, { ReactElement, Ref } from 'react';

import PropTypes from 'prop-types';

import { defaultLabelGetter } from '../../helpers/defaultGetters';
import { defaultRenderItems } from '../../helpers/defaultRenderers';
import useAutocompleteControl from '../../hooks/useAutocompleteControl';
import { SearchInput, ControlContainer } from '../../layout';
import { MultiControlProps } from '../../layout/Control/MultiControl';
import { GenericItem } from '../../types';

export type MultiAutocompleteControlProps<TItem extends GenericItem> = Omit<
  MultiControlProps<TItem>,
  'renderPlaceholder' | 'renderInput'
> & {
  openMenu?: () => void;
} & {
  softLimit?: number;
  maxLength?: number;
  showCount?: boolean;
  countDisplayThreshold?: number;
};

const MultiAutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    selectedItems,
    isOpen,
    disabled,
    openMenu,
    getInputProps,
    itemToString = defaultLabelGetter,
    placeholder: placeholderProp,
    removeSelectedItem: _removeSelectedItem,
    getSelectedItemProps: _getSelectedItemProps,
    renderItems = defaultRenderItems,
    softLimit,
    maxLength,
    showCount,
    countDisplayThreshold,
    ...props
  }: MultiAutocompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const { containerProps, searchInputProps } = useAutocompleteControl({
    placeholderProp,
    isOpen,
    disabled,
    openMenu,
    selectedItems,
  });

  const showItemCount =
    !isOpen && !!selectedItems?.length && selectedItems.length > 0;

  return (
    <ControlContainer ref={ref} {...containerProps} {...props}>
      {showItemCount &&
        renderItems({
          isOpen,
          selectedItems,
          getSelectedItemProps: _getSelectedItemProps,
          removeSelectedItem: _removeSelectedItem,
          itemToString,
          disabled,
        })}
      <SearchInput
        style={{ ...(showItemCount && { display: 'none' }) }}
        isOpen={isOpen}
        {...getInputProps?.(searchInputProps)}
        showIcon={isOpen}
        softLimit={softLimit}
        maxLength={maxLength}
        showCount={showCount}
        countDisplayThreshold={countDisplayThreshold}
      />
    </ControlContainer>
  );
};

const MultiAutocompleteControl = React.forwardRef(
  MultiAutocompleteControlWrapped,
);

MultiAutocompleteControl.displayName = 'MultiAutocompleteControl';

MultiAutocompleteControl.propTypes = {
  /** Function to open menu.  Provided by useMultiAutocomplete hook */
  openMenu: PropTypes.func.isRequired,
};

export default MultiAutocompleteControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
